import { useState } from 'react';
import type { Book } from '@/api/books';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';

/** Every error code that has words of its own; anything else reads as send_failed. */
const KNOWN_ERRORS = new Set([
    'not_configured',
    'not_found',
    'too_large',
    'conversion_failed',
    'send_failed',
    'in_progress',
    'unavailable',
    'connection_lost',
    'busy',
    'session_expired',
]);

/**
 * useSendToKindle is the "Kindle" button's behaviour for a whole list of
 * cards: whether the reader has a Kindle to send to, which books are on their
 * way, the question asked before one is sent, and the setup dialog for a
 * reader who has not set one up yet.
 *
 * A reader with no Kindle address gets the dialog and nothing goes to the
 * server: there is nothing it could do with the request but refuse it.
 *
 * A reader with one is asked first. A press used to send at once, and a book
 * sent by a stray tap is a mail in someone's Kindle library that cannot be
 * taken back.
 */
export const useSendToKindle = () => {
    const { user, logout } = useAuth();
    const { sendToKindle } = useWebSocket();
    const { t } = useTranslation();
    const [sending, setSending] = useState<ReadonlySet<number>>(() => new Set());
    const [setupOpen, setSetupOpen] = useState(false);
    // The book the reader is asked about. Kept after the answer, so the
    // question does not empty while it animates shut.
    const [pending, setPending] = useState<Book | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const configured = Boolean(user?.kindle_email);

    /** request is the card button's press: the setup, or the question. */
    const request = (book: Book) => {
        if (!configured) {
            setSetupOpen(true);
            return;
        }
        if (sending.has(book.id)) {
            return;
        }
        setPending(book);
        setConfirmOpen(true);
    };

    const send = async (bookID: number) => {
        if (!configured) {
            setSetupOpen(true);
            return;
        }
        if (sending.has(bookID)) {
            return;
        }
        setSending((current) => new Set(current).add(bookID));
        try {
            const reply = await sendToKindle(bookID);
            if (reply.status === 'sent') {
                // Sent, never delivered: Amazon drops a message from a sender
                // that is not approved without a word to anyone, so the hint
                // says where to look if the book does not turn up.
                toast.success(t('sendToKindle.sent'), {
                    description: t('sendToKindle.sentHint'),
                    duration: 8000,
                });
                return;
            }
            const code = reply.code && KNOWN_ERRORS.has(reply.code) ? reply.code : 'send_failed';
            if (code === 'in_progress') {
                toast.info(t('sendToKindle.error.in_progress'));
                return;
            }
            if (code === 'session_expired') {
                // Only after the socket provider tried to renew the session
                // and could not: it is really gone. Signing out the way the
                // Logout button does clears the reader here too — sending
                // them to /login while still signed in only bounced them back
                // to the catalogue.
                toast.error(t('sendToKindle.error.session_expired'));
                logout();
                return;
            }
            toast.error(t(`sendToKindle.error.${code}`));
            if (code === 'not_configured') {
                // The address was removed somewhere else since this page
                // loaded: show the reader how to put it back.
                setSetupOpen(true);
            }
        } finally {
            setSending((current) => {
                const next = new Set(current);
                next.delete(bookID);
                return next;
            });
        }
    };

    return {
        configured,
        /** Where the book goes: said in the question, so it is checked once more. */
        address: user?.kindle_email ?? '',
        isSending: (bookID: number) => sending.has(bookID),
        request,
        pending,
        confirmOpen,
        /** Sends the book asked about; only the confirmation calls this. */
        confirm: () => {
            if (!pending || !confirmOpen) return;
            setConfirmOpen(false);
            void send(pending.id);
        },
        cancel: () => setConfirmOpen(false),
        /** Sends without asking: what the confirmation does once answered. */
        send,
        setupOpen,
        closeSetup: () => setSetupOpen(false),
    };
};
